import styles from "./Designer.module.css";

const renderLittleBlueBold = (text) => {
    const value = String(text || "");
    const normalized = value.replace(/littleblue/gi, "Little Blue");
    const parts = normalized.split(/(Little Blue)/gi);
    return parts.map((part, index) => (
        /little blue/i.test(part)
            ? <strong key={`lb-${index}`}>Little Blue</strong>
            : <span key={`txt-${index}`}>{part}</span>
    ));
};

const formatCustomCutColorLabel = (color = null) => {
    const name = String(color?.name || "").trim();
    return name || "Color aplicado";
};

const DesignerFinalStepSection = ({
    imageLegalDisclaimer,
    garmentName = "Tu prenda",
    blockingIssues = [],
    appliedPartsBySection = [],
    appliedPrintsBySection = [],
    saveMsg = null,
    saveMsgLooksSuccess = false,
    saveBlockedByExistingDatasheet = false,
    datasheetGuardMessage = null,
    sketchGenerating = false,
    sketchError = null,
    sketchImage = null,
    finalPreviewImage = null,
    finalPreviewLoading = false,
    onSave,
    onBack,
    saving = false,
    saveDisabled = false,
    summaryPdfUrl = "",
    stripeStrokeSummary = null,
    customCutSummary = null,
    observations = "",
    onObservationsChange = null,
}) => {
    const stripeTotal = Number(stripeStrokeSummary?.total || 0);
    const stripeStraight = Number(stripeStrokeSummary?.straight || 0);
    const stripeSerpentinas = Number(stripeStrokeSummary?.curved || 0);
    const customCutTotal = Number(customCutSummary?.total || 0);
    const customCutDetails = Array.isArray(customCutSummary?.details) && customCutSummary.details.length > 0
        ? customCutSummary.details
        : Array.from({ length: customCutTotal }, (_, index) => ({
            id: `custom-cut-fallback-${index + 1}`,
            label: `Recorte ${index + 1}`,
            colors: [],
        }));
    // ---- Resumen "así se va a producir" para el cliente ----
    const allPartItems = (appliedPartsBySection || []).flatMap((g) => g.items || []);
    const stripeColors = Array.isArray(stripeStrokeSummary?.colors) ? stripeStrokeSummary.colors : [];
    const printItems = (appliedPrintsBySection || []).flatMap((g) => (g.items || []).filter((it) => !it.isStripeSummary));
    // Colores por parte: cada parte que tiene color, para la grilla de "Color de cada parte".
    const partColorItems = allPartItems.filter((it) => it.colorName || it.colorCode);

    return (
        <section className={`${styles.sectionCard} ${styles.finalStepCard}`}>
            <div className={styles.warningNotice}>
                <span className={styles.warningNoticeIcon} aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none">
                        <path d="M12 3l9 16H3L12 3z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                        <path d="M12 9v5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        <circle cx="12" cy="17" r="1.1" fill="currentColor" />
                    </svg>
                </span>
                <span>{renderLittleBlueBold(imageLegalDisclaimer)}</span>
            </div>

            {/* Preview frente + espalda (lado a lado) arriba de todo el resumen. */}
            {finalPreviewLoading ? (
                <div className={styles.fsPreviewWrap}>
                    <div className={styles.fsPreviewLoading}>Generando la vista de tu prenda…</div>
                </div>
            ) : finalPreviewImage ? (
                <div className={styles.fsPreviewWrap}>
                    <img src={finalPreviewImage} alt="Frente y espalda de tu prenda" className={styles.fsPreviewImg} />
                    <div className={styles.fsPreviewCaption}>Frente y espalda</div>
                </div>
            ) : null}

            {/* Resumen "así se va a producir" — secciones con tarjetas (estilo maqueta). */}
            <div className={styles.fsSummary}>
                <div className={styles.fsIntro}>
                    <b>Así se va a producir tu prenda</b>
                    <span>Revisá el boceto y comparalo con este detalle. Cada color, inscripción y técnica aparece explicado por separado.</span>
                </div>

                <section className={styles.fsSection}>
                    <div className={styles.fsSectionHead}>
                        <div className={styles.fsSectionIcon}>👕</div>
                        <div className={styles.fsSectionTitle}>
                            <b>Modelo y construcción</b>
                            <small>El artículo elegido para tu prenda.</small>
                        </div>
                    </div>
                    <div className={styles.fsSectionBody}>
                        <div className={styles.fsStructGrid}>
                            <div className={styles.fsStructRow}><span>Modelo</span><strong>{garmentName}</strong></div>
                        </div>
                    </div>
                </section>

                <section className={styles.fsSection}>
                    <div className={styles.fsSectionHead}>
                        <div className={styles.fsSectionIcon}>🎨</div>
                        <div className={styles.fsSectionTitle}>
                            <b>Color de cada parte</b>
                            <small>La muestra de color corresponde a la parte indicada.</small>
                        </div>
                    </div>
                    <div className={styles.fsSectionBody}>
                        {partColorItems.length > 0 ? (
                            <div className={styles.fsColorsGrid}>
                                {partColorItems.map((it, i) => (
                                    <div key={`fc-${it.key || i}`} className={styles.fsColorCard}>
                                        <span className={styles.fsColorSwatch} style={it.colorCode ? { background: it.colorCode } : undefined} />
                                        <span className={styles.fsColorText}>
                                            <small>{it.label}</small>
                                            <b>{it.colorName || "Color"}</b>
                                        </span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className={styles.fsEmpty}>Se respetan los colores del diseño base.</div>
                        )}
                    </div>
                </section>

                <section className={styles.fsSection}>
                    <div className={styles.fsSectionHead}>
                        <div className={styles.fsSectionIcon}>✂️</div>
                        <div className={styles.fsSectionTitle}>
                            <b>Tiras y recortes</b>
                            <small>Detalles incorporados a la construcción de la prenda.</small>
                        </div>
                    </div>
                    <div className={styles.fsSectionBody}>
                        <div className={styles.fsDetailList}>
                            {stripeTotal > 0 ? (
                                <div className={styles.fsDetailCard}>
                                    <div className={styles.fsDetailCode}>T</div>
                                    <div className={styles.fsDetailMain}>
                                        <h4>Tiras aplicadas</h4>
                                        <p>{stripeTotal} trazo{stripeTotal === 1 ? "" : "s"} · {stripeStraight} recta{stripeStraight === 1 ? "" : "s"} · {stripeSerpentinas} curva{stripeSerpentinas === 1 ? "" : "s"}</p>
                                        {stripeColors.length > 0 ? (
                                            <div className={styles.fsDetailLine}>
                                                <b>Colores:</b>
                                                {stripeColors.map((c, i) => (
                                                    <span key={`ts-${i}`} className={styles.fsMiniSwatch} style={c?.code ? { background: c.code } : undefined} />
                                                ))}
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                            ) : null}
                            {customCutDetails.map((detail, i) => (
                                <div key={`cc-${detail.id}`} className={styles.fsDetailCard}>
                                    <div className={styles.fsDetailCode}>{`R${i + 1}`}</div>
                                    <div className={styles.fsDetailMain}>
                                        <h4>Recorte personalizado</h4>
                                        <p>Divide visualmente esa parte de la prenda.</p>
                                        <div className={styles.fsDetailLine}>
                                            <b>Color:</b>
                                            {(detail.colors || []).length > 0 ? detail.colors.map((c, ci) => (
                                                <span key={`ccc-${ci}`} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                                                    <span className={styles.fsMiniSwatch} style={c?.code ? { background: c.code } : undefined} />
                                                    {formatCustomCutColorLabel(c)}
                                                </span>
                                            )) : <span>Sin color</span>}
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {stripeTotal === 0 && customCutDetails.length === 0 ? (
                                <div className={styles.fsEmpty}>No se agregaron tiras ni recortes personalizados.</div>
                            ) : null}
                        </div>
                    </div>
                </section>

                <section className={styles.fsSection}>
                    <div className={styles.fsSectionHead}>
                        <div className={styles.fsSectionIcon}>✦</div>
                        <div className={styles.fsSectionTitle}>
                            <b>Diseños, palabras, números y escudos</b>
                            <small>Qué se coloca, cómo se realiza y su ubicación.</small>
                        </div>
                    </div>
                    <div className={styles.fsSectionBody}>
                        <div className={styles.fsDetailList}>
                            {printItems.length > 0 ? printItems.map((it, i) => (
                                <div key={`d-${it.key || i}`} className={styles.fsDetailCard}>
                                    <div className={styles.fsDetailCode}>{`D${i + 1}`}</div>
                                    <div className={styles.fsDetailMain}>
                                        <h4>{it.techniqueLabel || "Diseño"}{it.locationLabel ? ` · ${it.locationLabel}` : ""}</h4>
                                        <p><b>{it.isText ? (it.textValue ? `“${it.textValue}”` : "Texto / número") : "Escudo o diseño"}</b></p>
                                        <div className={styles.fsTags}>
                                            {it.techniqueLabel ? <span className={`${styles.fsTag} ${styles.tech}`}>{it.techniqueLabel}</span> : null}
                                            <span className={styles.fsTag}>{it.isPersonal ? "Uno por persona" : "Todos igual"}</span>
                                            {it.locationLabel ? <span className={`${styles.fsTag} ${styles.side}`}>{it.locationLabel}</span> : null}
                                        </div>
                                    </div>
                                </div>
                            )) : <div className={styles.fsEmpty}>No se agregaron diseños o inscripciones.</div>}
                        </div>
                    </div>
                </section>

                <div className={styles.fsFinalCheck}>
                    <div className={styles.fsFinalCheckIcon}>✓</div>
                    <div>
                        <b>Antes de finalizar</b>
                        <span>Confirmá que los colores del boceto, los textos, las técnicas y el lado de colocación coincidan con lo que querés.</span>
                    </div>
                </div>

                {/* Observaciones del cliente: lo que no se pudo resolver dentro de la herramienta.
                    Viaja como `comments` → se guarda en datasheets.notes para que lo vea Diseño. */}
                <div style={{ marginTop: 14, padding: 16, borderRadius: 12, background: "#f0f7ff", border: "1px solid #cfe0f5" }}>
                    <label htmlFor="designer-observations" style={{ display: "block", fontWeight: 700, fontSize: 14, color: "#1e3a5f", marginBottom: 4 }}>
                        ¿Hay algo que no pudiste hacer en la herramienta?
                    </label>
                    <p style={{ margin: "0 0 8px", fontSize: 13, color: "#44607a" }}>
                        Dejanos acá cualquier aclaración o pedido especial y nuestro equipo de diseño lo resuelve. (Opcional)
                    </p>
                    <textarea
                        id="designer-observations"
                        value={observations}
                        onChange={(e) => onObservationsChange?.(e.target.value)}
                        disabled={saving || typeof onObservationsChange !== "function"}
                        rows={3}
                        maxLength={1000}
                        placeholder="Ej: el escudo va más chico, el nombre en dorado, quiero sumar la bandera de…, etc."
                        style={{ width: "100%", boxSizing: "border-box", resize: "vertical", padding: "10px 12px", borderRadius: 10, border: "1px solid #c3d3e6", fontSize: 14, fontFamily: "inherit", background: "#fff", color: "#1f2937" }}
                    />
                </div>
            </div>

            {blockingIssues.length > 0 ? (
                <div className={styles.printErrorsBox}>
                    {blockingIssues.map((issue, idx) => (
                        <div key={`blocking-${idx}`} className={styles.summaryHint}>
                            {issue.message}
                        </div>
                    ))}
                </div>
            ) : (
                null
            )}
            {saveMsg ? (
                <div className={saveMsgLooksSuccess ? styles.successHint : styles.infoHint}>
                    {renderLittleBlueBold(saveMsg)}
                </div>
            ) : null}
            {saveBlockedByExistingDatasheet && datasheetGuardMessage ? (
                <div className={styles.infoHint}>{renderLittleBlueBold(datasheetGuardMessage)}</div>
            ) : null}
            {summaryPdfUrl ? (
                <div className={styles.finalStepActions}>
                    <a className={styles.btn} href={summaryPdfUrl} target="_blank" rel="noreferrer">
                        Descargar resumen
                    </a>
                </div>
            ) : null}
            <div className={styles.finalStepActions}>
                {typeof onBack === "function" ? (
                    <button
                        type="button"
                        className={`${styles.btn} ${styles.btnGhost}`}
                        onClick={onBack}
                        disabled={saving}
                    >
                        <span className={styles.btnIconSvg} aria-hidden="true">
                            <svg viewBox="0 0 24 24" fill="none">
                                <path d="M14 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </span>
                        Volver a corregir
                    </button>
                ) : null}
                <button
                    type="button"
                    className={styles.btn}
                    onClick={onSave}
                    disabled={saving || saveDisabled}
                >
                    <span className={styles.btnIconSvg} aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none">
                            <path d="M5 4h11l3 3v13H5V4z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                            <path d="M8 4v6h8V4" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                            <path d="M8 16h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                    </span>
                    {saving ? "Guardando..." : "Confirmar"}
                </button>
            </div>
        </section>
    );
};

export default DesignerFinalStepSection;
